import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import type { SapBondStatusData, SapBondStatusRow } from '@/types/approval';
import { SapBondStatusRoot } from './styles';

type HeadCell = { label: string; rowSpan?: number; colSpan?: number };

type DetailTable = {
  key: Exclude<keyof SapBondStatusData, 'ET_DATA'>;
  title: string;
  cols: number[];
  head: HeadCell[][];
  fields: string[];
};

/** 상세표 6개 — 열 폭·머리글·필드 순서는 레거시 그대로 */
const DETAIL_TABLES: DetailTable[] = [
  {
    key: 'IT_DATA1',
    title: '철수 후 미정산 내역',
    cols: [70, 180, 220, 106, 59, 73, 60],
    head: [
      [
        { label: '매장코드' },
        { label: '매장명' },
        { label: '매장약어명' },
        { label: '폐점일자' },
        { label: '사업자' },
        { label: '채권잔액' },
        { label: '담보계' },
      ],
    ],
    fields: [
      'SHOP_CD',
      'SHOP_NM',
      'SHOP_ABBR_NM',
      'LIQUID_DAT',
      'REPR_NM',
      'AMOUNT',
      'TOT_COLL_AMT',
    ],
  },
  {
    key: 'IT_DATA2',
    title: '중간관리 교체 내역',
    cols: [70, 180, 180, 106, 73, 150],
    head: [
      [
        { label: '매장코드' },
        { label: '매장명' },
        { label: '매장약어명' },
        { label: '교체일자' },
        { label: 'SM CODE' },
        { label: 'SM 성명' },
      ],
    ],
    fields: [
      'SHOP_CD',
      'SHOP_NM',
      'SHOP_ABBR_NM',
      'ED_DATE',
      'SM_CODE',
      'SM_NAME',
    ],
  },
  {
    key: 'IT_DATA3',
    title: '대리점 미수 내역',
    cols: [130, 70, 220, 220, 106, 100, 100, 100, 100, 100, 110, 100],
    head: [
      [
        { label: '수금유형', rowSpan: 2 },
        { label: '매장코드', rowSpan: 2 },
        { label: '매장명', rowSpan: 2 },
        { label: '매장약어명', rowSpan: 2 },
        { label: '이월미수', rowSpan: 2 },
        { label: '조회년 월', colSpan: 5 },
        { label: '미수잔액(익월)', rowSpan: 2 },
        { label: '채권잔액', rowSpan: 2 },
      ],
      [
        { label: '매출' },
        { label: '수금예상' },
        { label: '입금' },
        { label: '입금율' },
        { label: '미수' },
      ],
    ],
    fields: [
      'VTEXT',
      'SHOP_CD',
      'SHOP_NM',
      'SHOP_ABBR_NM',
      'AMT01',
      'AMT02',
      'AMT03',
      'AMT04',
      'RATIO',
      'AMT05',
      'AMT06',
      'AMT07',
    ],
  },
  {
    key: 'IT_DATA4',
    title: '여신내역 - 사입점',
    cols: [105, 105, 70, 220, 110, 110, 110, 110, 110, 110, 110],
    head: [
      [
        { label: '관리자명', rowSpan: 2 },
        { label: '대표 이름', rowSpan: 2 },
        { label: '매장코드', rowSpan: 2 },
        { label: '매장명', rowSpan: 2 },
        { label: '담보총액', rowSpan: 2 },
        { label: '전전월잔액', rowSpan: 2 },
        { label: '전월잔액', rowSpan: 2 },
        { label: '당월', colSpan: 3 },
        { label: '여신초과액', rowSpan: 2 },
      ],
      [{ label: '출고' }, { label: '입금' }, { label: '잔액' }],
    ],
    fields: [
      'SM_NAME',
      'REPRES',
      'ALTKN',
      'ALTKN_T',
      'TOT_COLL_AMT',
      'PRE_CARRY_OVER_AMT',
      'CARRY_OVER_AMT',
      'GI_AMT',
      'DEPOSIT_AMT',
      'BALANCE',
      'SURPLUS_AMT',
    ],
  },
  {
    key: 'IT_DATA5',
    title: '여신내역 - 대리점',
    cols: [105, 70, 220, 120, 100, 120, 60, 110, 110, 110, 110, 110],
    head: [
      [
        { label: '대표 이름' },
        { label: '매장코드' },
        { label: '매장명' },
        { label: '보증금' },
        { label: '담보(부동산+보증보험+기타)' },
        { label: '담보계' },
        { label: '배수' },
        { label: '여신금액' },
        { label: '출고한도' },
        { label: '전월잔액' },
        { label: '당월잔액' },
        { label: '여신초과액' },
      ],
    ],
    fields: [
      'REPRES',
      'ALTKN',
      'ALTKN_T',
      'CASH_DEPOSIT',
      'COLLAT_AMT',
      'SUM_COLLAT_AMT',
      'MULTIPLE',
      'TOT_COLL_AMT',
      'CREDIT_AMT',
      'CARRY_OVER_AMT',
      'BALANCE',
      'SURPLUS_AMT',
    ],
  },
  {
    key: 'IT_DATA6',
    title: '여신내역 - 매장별 담보 현황',
    cols: [70, 220, 105, 110, 110, 110, 130, 110, 130, 110],
    head: [
      [
        { label: '매장코드', rowSpan: 2 },
        { label: '매장명', rowSpan: 2 },
        { label: '대표 이름', rowSpan: 2 },
        { label: '보증금', rowSpan: 2 },
        { label: '부동산' },
        { label: '보증보험', colSpan: 2 },
        { label: '기타담보', colSpan: 2 },
        { label: '담보계', rowSpan: 2 },
      ],
      [
        { label: '금액' },
        { label: '금액' },
        { label: '만료일' },
        { label: '금액' },
        { label: '만료일' },
      ],
    ],
    fields: [
      'ALTKN',
      'ALTKN_T',
      'REPRES',
      'CASH_DEPOSIT',
      'SECURITYAMT',
      'SURETY_AMT',
      'SURETY_TODATE',
      'OTHERSECURITYAMT',
      'TOAVAILABLEDATE',
      'SUM_COLLAT_AMT',
    ],
  },
];

const cell = (row: SapBondStatusRow, field: string) => {
  const value = row[field];
  return value === null || value === undefined ? '' : String(value);
};

const ConditionTable = ({
  brandName,
  period,
  t,
}: {
  brandName: string;
  period: string;
  t: TFunction;
}) => (
  <section>
    <table>
      <colgroup>
        <col width="50%" />
        <col width="50%" />
      </colgroup>
      <tbody>
        <tr>
          <th className="text-center">{t('브랜드')}</th>
          <th className="text-center">{t('기간')}</th>
        </tr>
        <tr>
          <td className="text-center">{brandName}</td>
          <td className="text-center">{period}</td>
        </tr>
      </tbody>
    </table>
  </section>
);

const SummaryTable = ({
  summary,
  t,
}: {
  summary: Record<string, string | null>;
  t: TFunction;
}) => {
  const sum = (id: string) => summary[id] ?? '';
  return (
    <section>
      <table>
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th className="text-center" colSpan={2}>
              {t('항목')}
            </th>
            <th className="text-center">{t('잔액/금액')}</th>
          </tr>
          <tr>
            <td className="text-left" rowSpan={2}>
              {t('미정산 현황')}
            </td>
            <td className="text-left">{t('철수 후 미정산점')}</td>
            <td className="text-left">{sum('SUM_A1')}</td>
          </tr>
          <tr>
            <td className="text-left">{t('중간관리 교체점')}</td>
            <td className="text-left">{sum('SUM_A2')}</td>
          </tr>
          <tr>
            <td className="text-left">{t('수금현황')}</td>
            <td className="text-left">{t('대리점 미수금 내역')}</td>
            <td className="text-left">{sum('SUM_B1')}</td>
          </tr>
          <tr>
            <td className="text-left" rowSpan={2}>
              {t('여신 및 담보현황')}
            </td>
            <td className="text-left">{t('상설점 여신내역')}</td>
            <td className="text-left">{sum('SUM_C1')}</td>
          </tr>
          <tr>
            <td className="text-left">{t('매장별 담보 및 보증금 내역')}</td>
            <td className="text-left">{sum('SUM_C2')}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
};

const DetailTableSection = ({
  table,
  rows,
  t,
}: {
  table: DetailTable;
  rows: SapBondStatusRow[];
  t: TFunction;
}) => (
  <section>
    <table className="text-center">
      <colgroup>
        {table.cols.map((width, i) => (
          <col key={i} width={`${width}px`} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="text-left table-title" colSpan={table.fields.length}>
            {t(table.title)}
          </th>
        </tr>
        {table.head.map((headRow, i) => (
          <tr key={i}>
            {headRow.map((head, j) => (
              <th key={j} rowSpan={head.rowSpan} colSpan={head.colSpan}>
                {t(head.label)}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row, i) => (
            <tr key={i}>
              {table.fields.map((field) => (
                <td key={field}>{cell(row, field)}</td>
              ))}
            </tr>
          ))
        ) : (
          <tr className="no-data">
            <td colSpan={table.fields.length}>{t('데이터가 없습니다.')}</td>
          </tr>
        )}
      </tbody>
    </table>
  </section>
);

type ContentProps = {
  brandName: string;
  period: string;
  /** null 이면 조회 실패 — 브랜드·기간 표만 */
  data: SapBondStatusData | null;
  t: TFunction;
};

const SapBondStatusContent = ({ brandName, period, data, t }: ContentProps) => (
  <>
    <label className="label">{t('채권현황')}</label>
    <ConditionTable brandName={brandName} period={period} t={t} />
    {data && (
      <>
        <SummaryTable summary={data.ET_DATA ?? {}} t={t} />
        {DETAIL_TABLES.map((table) => (
          <DetailTableSection
            key={table.key}
            table={table}
            rows={data[table.key] ?? []}
            t={t}
          />
        ))}
      </>
    )}
  </>
);

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildSapBondStatusContentHtml = (props: ContentProps): string =>
  renderToStaticMarkup(<SapBondStatusContent {...props} />);

// 레거시 문서 content 에는 조회 컨트롤(kendo 입력칸·확인 버튼·로딩 문구)이 섞여 있음
const DROP_TAGS = new Set(['style', 'script', 'link', 'input', 'button']);
const DROP_CLASSES = ['k-widget', 'loading-msg'];
const TABLE_PARTS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'colgroup',
]);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

type SnapshotProps = {
  content: string;
  /** 작성 화면은 필드 머리글이 따로 있어 저장 HTML 의 제목을 숨김 */
  hideTitle?: boolean;
};

/** 저장 content(레거시 문서 포함) 그대로 — 표만 공통 양식 표로 */
export const SapBondStatusSnapshot = ({
  content,
  hideTitle,
}: SnapshotProps) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (DROP_CLASSES.some((c) => element.classList.contains(c))) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      const key = props.key as string;
      // 제목 — V2 는 label, 레거시는 .form-header h2
      if (tagName === 'label' || tagName === 'h2') {
        if (hideTitle) return null;
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (tagName === 'table') {
        return (
          <ApprovalFormTable key={key} className={props.className as string}>
            {children}
          </ApprovalFormTable>
        );
      }
      return undefined;
    },
    [hideTitle],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <SapBondStatusRoot>{rendered}</SapBondStatusRoot>;
};
